import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { cameraRight, eyeHeight, lookPoint, Precip, SkyLook, type OmenCtx, type OmenScene } from './common.ts';

/**
 * Frenzy: a hot red light that beats like a heart (two quick throbs and a rest), embers rising, the
 * nightmares' eyes burning and red afterimages streaking behind them as they run, dust kicked up at
 * their feet.
 */

const EMBER = new THREE.Color('#ff4f3a');
const HOT = new THREE.Color('#ff8a6e');
const EYE = new THREE.Color('#ffd0b0');
const TRAIL = 5;

export const buildFrenzy = (ctx: OmenCtx): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-frenzy';
  const low = ctx.low;
  const sky = new SkyLook(ctx, {
    hemi: ['#b8a4a0', '#2a1614', 0.9],
    key: ['#ffd2bc', 0.95],
    rim: ['#ff4f3a', 1.8],
    background: '#2a1512',
    fog: '#2a1512',
    mist: { low: '#4a2a26', mid: '#36201c', far: '#2a1512', alpha: 1.0 },
  });
  const embers = new Precip({ count: low ? 120 : 360, box: [13, 5, 13], fall: -0.9, sway: 0.25, size: [0.045, 0.045], color: '#ff6a3a', opacity: 0.85, round: true, additive: true, twinkle: 6 }, 61);
  const glow = new GlowLayer(1536);
  group.add(embers.mesh, glow.mesh);
  /** Where each nightmare was on the last few ticks (world points, newest first). */
  const trails = new Map<number, THREE.Vector3[]>();
  let enemies: { id: number; kind: string }[] = [];
  let tickN = 0;
  const look = new THREE.Vector3();
  const right = new THREE.Vector3();

  return {
    group,
    tick: (s: BattleState) => {
      tickN++;
      enemies = [];
      const seen = new Set<number>();
      let dust = 0;
      for (const e of s.enemies) {
        if (e.hp <= 0 || e.kind === 'boneWall') continue;
        seen.add(e.id);
        enemies.push({ id: e.id, kind: e.kind });
        const p = ctx.at(e.x, e.y, 0);
        const tr = trails.get(e.id) ?? [];
        const moved = !tr[0] || tr[0].distanceToSquared(p) > 0.0004;
        tr.unshift(p);
        if (tr.length > TRAIL) tr.pop();
        trails.set(e.id, tr);
        if (moved && dust < (low ? 3 : 8) && (tickN + e.id) % 4 === 0) {
          dust++;
          ctx.vfx.burst(p.clone().setY(p.y + 0.05), { n: 1, color: '#5a3a30', speed: 0.02, up: 0.02, size: 0.25, life: 8, smoke: true, spread: 0.1 });
        }
      }
      for (const id of [...trails.keys()]) if (!seen.has(id)) trails.delete(id);
    },
    frame: (t, camera) => {
      lookPoint(camera, look);
      embers.frame(t, look);
      // A heartbeat: two throbs, a rest.
      const ph = (t * 1.25) % 1;
      const beat = Math.exp(-Math.pow((ph - 0.08) * 18, 2)) + 0.6 * Math.exp(-Math.pow((ph - 0.3) * 18, 2));
      sky.pulse({ hemi: 0.9 + 0.35 * beat, rim: 0.85 + 0.6 * beat, key: 0.95 + 0.15 * beat });
      cameraRight(camera, right);
      glow.begin();
      for (const e of enemies) {
        const p = ctx.pos(e.id);
        const tr = trails.get(e.id);
        if (!p || !tr) continue;
        const y = eyeHeight(e.kind);
        // Afterimages: red ghosts of where it just was.
        for (let i = 1; i < tr.length; i++) {
          const q = tr[i] as THREE.Vector3;
          if (q.distanceToSquared(p) < 0.01) continue;
          const f = 1 - i / TRAIL;
          glow.add(q.x, q.y + y * 0.7, q.z, EMBER, 0.55 * f + 0.15, 0.35 * f);
        }
        for (const sgn of [-1, 1]) glow.add(p.x + right.x * 0.05 * sgn, p.y + y, p.z + right.z * 0.05 * sgn, EYE, 0.09, 0.95);
        glow.add(p.x, p.y + y, p.z, HOT, 0.35, 0.3 + 0.2 * beat);
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      embers.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
